import { useSnackbar } from 'notistack'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSetCurrencyMutation } from '@graphql/mutations/__generated__/user-translation.generated'
import { useApolloClient } from '@apollo/client'
import Autocomplete from '@mui/material/Autocomplete'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import useLocalStorage from '../../../hooks/useLocalStorage'
import { LOCAL_STORAGE_KEYS } from '../../../../constants'
import { useCurrentCurrencyExchangeRateQuery } from '@graphql/queries/__generated__/shipping.generated'
import { CURRENCY_OPTIONS } from '../../../../mappings'

const LANGUAGES = ['English', 'Ukrainian']

interface HeaderCurrencyProps {
  isHover: boolean
}

const HeaderCurrencyPanel: React.FC<HeaderCurrencyProps> = ({ isHover }) => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const client = useApolloClient()
  const [storageCurrency, setStorageCurrency, getStorageCurrency] = useLocalStorage(LOCAL_STORAGE_KEYS.CURRENCY, 'USD')
  const [isLoading, setIsLoading] = useState(false)

  const [currentCurrency, setCurrentCurrency] = useState(storageCurrency)
  const [currentLanguage, setCurrentLanguage] = useState('English')

  const [setCurrency] = useSetCurrencyMutation({
    onCompleted: () => {
      client.refetchQueries({ include: 'active' })
    },
    onError: e => {
      console.error(e)
      enqueueSnackbar('Something went wrong.', { variant: 'error' })
    },
  })

  const currencyExchangeRateQuery = useCurrentCurrencyExchangeRateQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
    onCompleted: data => {
      // if (getStorageCurrency() && data?.currentCurrencyExchangeRate?.currencyCode !== getStorageCurrency()) {
      //   handleChangeCurrency(getStorageCurrency())
      // }
      setStorageCurrency(data?.currentCurrencyExchangeRate?.currencyCode || 'USD')
    },
    onError: e => {
      console.error(e)
      setCurrentCurrency('USD')
      setStorageCurrency('USD')
    },
  })

  const handleChangeCurrency = (value: string) => {
    setCurrentCurrency(value)
    handleSave(value)
  }

  const handleSave = (currency: string) => {
    setIsLoading(true)
    setCurrency({ variables: { currency } }).finally(() => {
      setStorageCurrency(currency)
      setIsLoading(false)
    })
  }

  return (
    <div>
      <div className='px-12 pt-24 shadow-md'>
        <div className='mb-20'>
          <label className='font-medium text-15 mb-8' htmlFor='currency'>
            {t('currency', 'Currency')}
          </label>
          <Autocomplete
            fullWidth
            disableClearable
            autoComplete
            options={CURRENCY_OPTIONS.map(option => option.value)}
            getOptionLabel={option => CURRENCY_OPTIONS.find(item => item.value === option)?.label || option.toString()}
            value={currentCurrency}
            onChange={(e, value: string) => {
              handleChangeCurrency(value)
            }}
            disableListWrap
            disabled={isLoading}
            isOptionEqualToValue={(option, value) => option === value}
            PaperComponent={params => <Paper {...params} elevation={4} />}
            ListboxProps={{
              sx: {
                maxHeight: '200px',
                overflowY: 'auto',
              },
            }}
            disablePortal
            renderInput={params => (
              <TextField
                {...params}
                name='currency'
                variant='outlined'
                color='secondary'
                margin='none'
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  readOnly: true,
                  autoComplete: 'new-password',
                  className: 'browser-default data-hj-allow ' + params.inputProps.className,
                }}
              />
            )}
          />
        </div>
        <div className='mb-20'>
          <label className='font-medium text-15 mb-8' htmlFor='language'>
            {t('language', 'Language')}
          </label>
          <Autocomplete
            fullWidth
            disableClearable
            autoComplete
            options={LANGUAGES}
            value={currentLanguage}
            onChange={(e, value: string) => {
              setCurrentLanguage(value)
            }}
            disabled
            disableListWrap
            isOptionEqualToValue={(option, value) => option === value}
            PaperComponent={params => <Paper {...params} elevation={4} />}
            ListboxProps={{
              sx: {
                maxHeight: '150px',
                overflowY: 'auto',
              },
            }}
            disablePortal
            getOptionLabel={option => option.toString()}
            renderInput={params => (
              <TextField
                {...params}
                name='currency'
                variant='outlined'
                color='secondary'
                margin='none'
                fullWidth
                inputProps={{
                  ...params.inputProps,
                  readOnly: true,
                  autoComplete: 'new-password',
                  className: 'browser-default data-hj-allow ' + params.inputProps.className,
                }}
              />
            )}
          />
        </div>
      </div>
    </div>
  )
}

export default HeaderCurrencyPanel
